import Entities from "./shared/Entities";
import L from "leaflet";
import europleGEOJSON from "../../../public/europe.geo.json";
import northAmericaGEOJSON from "../../../public/north-america.geo.json";
import australiaGEOJSON from "../../../public/australia.geo.json";

const geoCache = {};

async function loadGeoJSON(path) {
  if (geoCache[path]) {
    return geoCache[path];
  }

  geoCache[path] = fetch(path).then((res) => {
    if (!res.ok) {
      throw new Error(`Failed to load GeoJSON: ${path}`);
    }
    return res.json();
  });

  return geoCache[path];
}

export default class InteractiveMap {
  constructor() {
    this.entities = new Entities("InteractiveMap", "[data-map]", InteractiveMap.initSingle, InteractiveMap.destroySingle);
  }

  static async initSingle(containerEl) {
    const maps = [];
    const mapEls = containerEl.querySelectorAll('[id^="map-thumbnail-"]');
    const countriesAttr = containerEl.dataset.countries || "";
    const activeCountries = countriesAttr.split(",").map((c) => c.trim().toLowerCase());
    
    const continentEls = containerEl.querySelectorAll('[id^="map-"]:not([id^="map-thumbnail"])');

    const citiesData = JSON.parse(containerEl.dataset.cities || "{}");

    let currentCountry = "";

    const activePanel = containerEl.querySelector("[data-panel][data-active]");

    const continentMap = {
      europe: "europa",
      "north america": "ameryka-polnocna",
      australia: "australia",
    };

    const countryAliases = {
      usa: "stany zjednoczone",
      "wielka-brytania": "wielka brytania",
    };

    const worldGeoJSON = await loadGeoJSON("/wp-content/themes/eduworld/public/thumbnail-map.geo.json");

    if (activePanel) {
      const activeId = activePanel.dataset.cityId;

      Object.values(citiesData).forEach((continent) => {
        continent.forEach((city) => {
          if (String(city.id) === activeId) {
            currentCountry = city.country.toLowerCase();
          }
        });
      });
    }

    function setActiveCity(id) {
      const panels = containerEl.querySelectorAll("[data-panel]");

      panels.forEach((panel) => {
        panel.toggleAttribute("data-active", panel.dataset.cityId === String(id));
      });
    }

    function setActiveCountry(country) {
      currentCountry = country.toLowerCase();

      worldLayers.forEach((layer) => {
        layer.eachLayer((featureLayer) => {
          const feature = featureLayer.feature;

          const countryNamePL = normalize(feature.properties.NAME_PL);

          const isActive = activeCountries.some((slug) => {
            const normalizedSlug = normalize(slug);
            const mapped = countryAliases[normalizedSlug] || normalizedSlug;

            return normalize(mapped) === normalize(countryNamePL);
          });

          const normalizedCurrent = normalize(currentCountry);
          const currentMapped = countryAliases[normalizedCurrent] || normalizedCurrent;

          const isCurrent = currentMapped === countryNamePL;

          featureLayer.setStyle({
            fillColor: isCurrent ? "#CDFC8A" : isActive ? "#FFFDF7" : "#C7C3B5",
            fillOpacity: 1,
            stroke: false,
          });
        });
      });
    }

    function setActiveContinent(name) {
      const continents = containerEl.querySelectorAll("[data-continent]");
      const mapped = continentMap[name];

      if (!mapped) return;

      continents.forEach((continent) => {
        continent.toggleAttribute("data-active", continent.dataset.continent === mapped);
      });

      const defaultCity = getDefaultCityForContinent(mapped);

      if (defaultCity) {
        setActiveCity(defaultCity.id);
        setActiveCountry(defaultCity.country);
      }
    }

    function getDefaultCityForContinent(continentSlug) {
      const cities = citiesData?.[continentSlug] || [];

      const defaultCity = cities.find((city) => city.isDefault);

      return defaultCity || cities[0];
    }

    function normalize(str) {
      return str
        ?.toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    }

    const worldLayers = [];

    const geoByContinent = {
      europa: europleGEOJSON,
      "ameryka-polnocna": northAmericaGEOJSON,
      australia: australiaGEOJSON,
    };

    mapEls.forEach((el) => {
      const map = L.map(el, {
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
      });

      const layer = L.geoJSON(worldGeoJSON, {
        style: (feature) => {
          const countryNamePL = normalize(feature.properties.NAME_PL);

          const isActive = activeCountries.some((slug) => {
            const normalizedSlug = normalize(slug);
            const mapped = countryAliases[normalizedSlug] || normalizedSlug;

            return mapped === countryNamePL;
          });

          const normalizedCurrent = normalize(currentCountry);
          const currentMapped = countryAliases[normalizedCurrent] || normalizedCurrent;

          const isCurrent = currentMapped === countryNamePL;

          return {
            fillColor: isCurrent ? "#CDFC8A" : isActive ? "#FFFDF7" : "#C7C3B5",
            fillOpacity: 1,
            stroke: false,
          };
        },

        onEachFeature: (feature, layer) => {
          layer.on("click", () => {
            let continentName = feature.properties.CONTINENT?.toLowerCase();

            if (continentName === 'oceania') continentName = 'australia';
            
            if (continentName) {
              setActiveContinent(continentName);
            }
          });
        },
      }).addTo(map);

      map.fitBounds(layer.getBounds());

      worldLayers.push(layer);
    });

    continentEls.forEach((el) => {
      const slug = el.id.replace("map-", "");

      const geoData = geoByContinent[slug];

      if (!geoData) return;

      const map = L.map(el, {
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        zoomSnap: 0,
      });

      const layer = L.geoJSON(geoData, {
        style: () => {
          return {
            fillColor: "#E7E7E7",
            fillOpacity: 0.5,
            stroke: false,
            color: "transparent",
            weight: 0,
            opacity: 0,
          };
        },
      }).addTo(map);

      const cityLayer = L.layerGroup().addTo(map);

      if (citiesData?.[slug]?.length) {
        citiesData[slug].forEach((city) => {
          const marker = L.marker([city.x, city.y], {
            icon: L.divIcon({
              className: "city-pulse-marker",
              html: `
          <div class="marker-inner">
            <div class="pulse"></div>
            <div class="hover-content-relative">
              <div class="hover-content">
                <div class="image">
                  <img src='${city.image}' alt="${city.mainCity}" />
                </div>
              </div>
              <div class="tooltip">
                <div class="tooltip-relative">
                  <img src="/wp-content/themes/eduworld/public/images/map-tooltip.svg" alt="Tooltip" />
                  <div class="tooltip-text">
                    <strong>${city.country}</strong>
                    <span>${city.mainCity}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `,
              iconSize: [16, 16],
              iconAnchor: [8, 8],
            }),
            cityId: city.id,
            cityName: city.name,
          }).addTo(cityLayer);

          marker.data = city;

          marker.on("click", () => {
            setActiveCity(marker.data.id);
            setActiveCountry(marker.data.country);
          });
        });
      }

      if (slug === "europa") {
        map.setView([54, 15], 3.5);
      } else if (slug === "ameryka-polnocna") {
        map.setView([40, -100], 2.5);
      } else {
        map.fitBounds(layer.getBounds());
      }

      maps.push(map);
    });

    setTimeout(() => {
      maps.forEach((map) => map.invalidateSize());
    }, 400);

    return {
      destroy() {
        maps.forEach((map) => map.remove());
      },
    };
  }

  static destroySingle({ entityObj }) {
    entityObj?.destroy();
  }
}
